import { toast } from "react-toastify";

const ALLOWED_FILE_TYPES = [
  "pdf",
  "jpg", "jpeg", "png", "tiff", "heic",
  "doc", "docx", "odt",
  "xlsx", "csv", "ods",
  "ppt", "pptx", "odp",
  "txt", "rtf"
];

export const validateFileTypes = (files, options = {}) => {
  const { showToast = true } = options;

  const result = {
    isValid: true,
    errors: [],
    validFiles: [],
    invalidFiles: [],
    fileCount: 0,
  };

  const filesArray = Array.from(files || []);
  result.fileCount = filesArray.length;
  const invalidExtensions = new Set();

  filesArray.forEach((file) => {
    const fileNameParts = file.name.split(".");
    const ext = fileNameParts.length > 1 ? fileNameParts.pop().toLowerCase() : "";

    // Case 1: No extension
    if (!ext) {
      const errorMsg = `File "${file.name}" has no extension`;
      result.invalidFiles.push({
        file,
        name: file.name,
        size: file.size,
        extension: ext,
        error: errorMsg
      });
      result.errors.push(errorMsg);
      invalidExtensions.add("NO EXTENSION");
      return;
    }

    // Case 2: Double / multiple extensions (Block everything with more than 1 dot)
    if (fileNameParts.length > 1) {
      const errorMsg = `File "${file.name}" has multiple extensions (not allowed)`;
      result.invalidFiles.push({
        file,
        name: file.name,
        size: file.size,
        extension: ext,
        error: errorMsg
      });
      result.errors.push(errorMsg);
      invalidExtensions.add("MULTIPLE EXTENSIONS");
      return;
    }

    // Allowed extensions (single valid extension only)
    if (ALLOWED_FILE_TYPES.includes(ext)) {
      result.validFiles.push({
        file,
        name: file.name,
        size: file.size,
        extension: ext
      });
    } else {
      // Not allowed extension
      const errorMsg = `File type "${ext.toUpperCase()}" is not supported`;
      result.invalidFiles.push({
        file,
        name: file.name,
        size: file.size,
        extension: ext,
        error: errorMsg
      });
      result.errors.push(errorMsg);
      invalidExtensions.add(ext.toUpperCase());
    }
  });


  result.isValid = result.validFiles.length > 0 && result.invalidFiles.length === 0;

  // Show toast for invalid files
  if (invalidExtensions.size > 0 && showToast) {
    toast.dismiss();
    const invalidTypes = Array.from(invalidExtensions).join(", ");
    const message =
      invalidExtensions.size === 1
        ? `File type "${invalidTypes}" is not supported`
        : `File types "${invalidTypes}" are not supported`;
    toast.error(message);
  }

  return result;
};

export const useFileTypeValidator = () => {
  const validateFiles = (files, options = {}) => {
    try {
      return validateFileTypes(files, options);
    } catch (error) {
      console.error("File validation error:", error);
      if (options.showToast !== false) {
        toast.dismiss();
        toast.error("Error validating files");
      }
      return {
        isValid: false,
        errors: ["Validation error occurred"],
        validFiles: [],
        invalidFiles: [],
        fileCount: 0,
      };
    }
  };

  const validateFileInput = (event, options = {}) => {
    if (!event || !event.target || !event.target.files) {
      if (options.showToast !== false) {
        toast.dismiss();
        toast.error("Invalid file input");
      }
      return {
        isValid: false,
        errors: ["Invalid file input"],
        validFiles: [],
        invalidFiles: [],
        fileCount: 0,
      };
    }
    return validateFiles(event.target.files, options);
  };

  const validateDropFiles = (files, options = {}) => {
    return validateFiles(files, options);
  };

  return {
    validateFiles,
    validateFileInput,
    validateDropFiles,
    ALLOWED_FILE_TYPES,
    getAcceptAttribute: () =>
      ALLOWED_FILE_TYPES.map((ext) => `.${ext}`).join(","),
    getAllowedTypesDisplay: () => ALLOWED_FILE_TYPES.join(", "),
  };
};

// Helper function to prevent form submission
function preventFormSubmission(e) {
  e.preventDefault();
  e.stopPropagation();
  return false;
}

// Updated Global validation function
export const enableGlobalFileValidation = (options = {}) => {
  const {
    showToast = true,
    resetOnError = true,
    onValidFiles,
    onInvalidFiles,
  } = options;

  // Remove existing listener to prevent duplicates
  document.removeEventListener("change", handleFileChange);
  document.removeEventListener("submit", handleFormSubmit);

  function handleFileChange(event) {
    try {
      const target = event.target;

      if (target && target.tagName === "INPUT" && target.type === "file") {
        const validation = validateFileTypes(target.files, { showToast });

        if (!validation.isValid) {
          // Prevent the file change event
          event.preventDefault();
          event.stopPropagation();

          // Clear the input value
          if (resetOnError) {
            target.value = "";
          }

          // Mark the input as having invalid files
          target.setAttribute('data-has-invalid-files', 'true');

          // Add temporary form submit listener to prevent upload
          const form = target.closest('form');
          if (form) {
            form.addEventListener('submit', preventFormSubmission, {
              once: true,
              capture: true
            });

            // Remove the prevention after 1 second (allow valid files later)
            setTimeout(() => {
              form.removeEventListener('submit', preventFormSubmission, {
                capture: true
              });
            }, 1000);
          }

          if (onInvalidFiles) onInvalidFiles(validation, target);
          return false;
        } else {
          // Remove invalid files marker
          target.removeAttribute('data-has-invalid-files');
          if (onValidFiles) onValidFiles(validation, target);
        }
      }
    } catch (error) {
      console.error("Global file validation error:", error);
      if (showToast) {
        toast.dismiss();
        toast.error("File validation error occurred");
      }
    }
  }

  // Global form submit handler
  function handleFormSubmit(event) {
    const form = event.target;
    const fileInputs = form.querySelectorAll('input[type="file"][data-has-invalid-files="true"]');

    if (fileInputs.length > 0) {
      event.preventDefault();
      event.stopPropagation();

      if (showToast) {
        toast.dismiss();
        toast.error("Please remove invalid files before submitting");
      }

      return false;
    }
  }

  // Add event listeners
  document.addEventListener("change", handleFileChange, { capture: true });
  document.addEventListener("submit", handleFormSubmit, { capture: true });

  // Cleanup function
  return () => {
    document.removeEventListener("change", handleFileChange, { capture: true });
    document.removeEventListener("submit", handleFormSubmit, { capture: true });
  };
};

// Custom Hook for individual file inputs
export const useFileInputWithValidation = () => {
  const handleFileChange = (event, onSuccess, onError) => {
    const validation = validateFileTypes(event.target.files, { showToast: true });

    if (!validation.isValid) {
      // Block the upload completely
      event.preventDefault();
      event.stopPropagation();
      event.target.value = "";

      if (onError) onError(validation);
      return null;
    }

    if (onSuccess) onSuccess(validation);
    return validation.validFiles;
  };

  return {
    handleFileChange,
    getAcceptAttribute: () => ALLOWED_FILE_TYPES.map((ext) => `.${ext}`).join(","),
    getAllowedTypes: () => ALLOWED_FILE_TYPES
  };
};

export default {
  validateFileTypes,
  useFileTypeValidator,
  enableGlobalFileValidation,
  useFileInputWithValidation,
  ALLOWED_FILE_TYPES
};
